'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { useUser } from '@/lib/context/user-context';
import { useUserSettings } from '@/contexts/user-settings-context';
import { toast } from "sonner";
import { 
  examTabs,
  examCardsData,
  vocabularyCategories,
  kaoyanCategories,
  professionalCategories,
  petCategories,
  selfStudyCategories,
  otherCategories,
  dictionaryMapping,
  createReverseDictionaryMapping,
  getDefaultDictionaryFile,
} from '@/lib/constants/vocabulary-categories';

interface Dictionary {
  id: string;
  name: string;
  path: string;
}

// Module-scoped cache to avoid repeated API calls during dev StrictMode remounts
// and across re-entries within the same session.
let DICT_CACHE: Dictionary[] | null = null;
let DICT_IN_FLIGHT: Promise<Dictionary[]> | null = null;

export const useVocabularySettings = () => {
  // State management
  const [selectedTab, setSelectedTab] = useState("college");
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedExamTab, setSelectedExamTab] = useState("TOEFL");
  const [availableDictionaries, setAvailableDictionaries] = useState<Dictionary[]>([]);
  const [selectedDictionary, setSelectedDictionary] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [dictionariesLoading, setDictionariesLoading] = useState(true);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [tabChangedFromSettings, setTabChangedFromSettings] = useState(false);
  const [isExamCategory, setIsExamCategory] = useState(false);
  const lastDictionaryRef = useRef<string | null>(null);

  const { user } = useUser();
  const { refreshUserSettings } = useUserSettings();

  // Create reverse mapping for dictionary lookups
  const reverseDictionaryMapping = createReverseDictionaryMapping();

  // Check if a category is selected
  const isCategorySelected = useCallback((cardId: string) => {
    if (!selectedDictionary) return false;

    // Direct match (id matches filename)
    if (selectedDictionary === cardId) return true;

    // Check via mapping
    if (dictionaryMapping[cardId] && dictionaryMapping[cardId] === selectedDictionary) return true;

    return false;
  }, [selectedDictionary]);


  // Update user's dictionary setting
  const updateDictionarySetting = useCallback(async (dictionaryId: string) => {
    if (!user?.id) {
      toast.error("Please log in to save vocabulary settings");
      return;
    }

    try {
      setIsLoading(true);

      // Update local state first for immediate feedback
      setSelectedDictionary(dictionaryId);

      // Save to API
      const response = await fetch("/api/user-settings", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId: user.id,
          settingKey: "dictionarySetting",
          value: dictionaryId,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to save settings: ${response.status}`);
      }

      toast.success("Vocabulary selection saved successfully!");
      console.log("Dictionary setting updated successfully to:", dictionaryId);
      
      // 立即刷新用户设置上下文，确保其他页面能获取到最新设置
      try {
        await refreshUserSettings();
        console.log("✅ User settings context refreshed after dictionary update");
      } catch (refreshError) {
        console.warn("⚠️ Failed to refresh user settings context:", refreshError);
        // 即使刷新失败也不影响主要流程
      }
    } catch (error) {
      console.error("Error updating dictionary setting:", error);
      toast.error("Failed to save vocabulary settings. Please try again.");
      // Revert local state on error
      setSelectedDictionary("");
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, refreshUserSettings]);

  // Handle card click in the college/graduate tabs
  const handleVocabularyCardClick = useCallback((categoryId: string) => {
    const dictionaryFile = getDefaultDictionaryFile(categoryId);
    updateDictionarySetting(dictionaryFile);
  }, [updateDictionarySetting]);

  // Handle card click in the exam tabs
  const handleExamCardClick = useCallback((cardId: string) => {
    const dictionaryFile = getDefaultDictionaryFile(cardId);
    updateDictionarySetting(dictionaryFile);
  }, [updateDictionarySetting]);

  // Handle removing dictionary selection
  const handleRemoveSelection = useCallback(async () => {
    if (!user?.id) {
      toast.error("Please log in to modify vocabulary settings");
      return;
    }

    try {
      setIsLoading(true);

      // Update local state first for immediate feedback
      setSelectedDictionary("");

      // Save to API
      const response = await fetch("/api/user-settings", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId: user.id,
          settingKey: "dictionarySetting",
          value: "",
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to save settings: ${response.status}`);
      }

      toast.success("Vocabulary selection removed successfully!");
      console.log("Dictionary setting removed successfully");
      
      // 立即刷新用户设置上下文
      try {
        await refreshUserSettings();
        console.log("✅ User settings context refreshed after dictionary removal");
      } catch (refreshError) {
        console.warn("⚠️ Failed to refresh user settings context:", refreshError);
      }
    } catch (error) {
      console.error("Error removing dictionary setting:", error);
      toast.error("Failed to remove vocabulary settings. Please try again.");
      // Revert local state on error - restore previous selection if there was one
      // For now, we'll leave it empty since we don't track the previous state
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, refreshUserSettings]);

  // Set the appropriate tab based on dictionary
  useEffect(() => {
    if (!settingsLoaded) return;

    if (!selectedDictionary) {
      setIsExamCategory(false);
      lastDictionaryRef.current = "";
      return;
    }

    if (lastDictionaryRef.current === selectedDictionary) {
      return;
    }

    lastDictionaryRef.current = selectedDictionary;

    const matchingCardIds = reverseDictionaryMapping[selectedDictionary] || [];

    if (matchingCardIds.length === 0) {
      setIsExamCategory(false);
      return;
    }

    const cardId = matchingCardIds[0];

    let newTab = "college";
    let resolvedExamTab: string | null = null;
    let isExam = false;

    if (
      cardId.startsWith("cet") ||
      cardId === "graduate" ||
      vocabularyCategories.some((c) => c.id === cardId)
    ) {
      newTab = "college";
    } else if (
      cardId.includes("kaoyan") ||
      kaoyanCategories.some((c) => c.id === cardId)
    ) {
      newTab = "graduate";
    } else if (cardId.includes("toefl")) {
      resolvedExamTab = "TOEFL";
      isExam = true;
    } else if (
      cardId.includes("ielts") ||
      cardId === "ielts-scene-vocab" ||
      cardId === "heqiong-ielts" ||
      cardId === "wangu807-listen-v2" ||
      cardId === "100-sentence-7000-words" ||
      cardId.startsWith("xdf-ielts-")
    ) {
      resolvedExamTab = "IELTS";
      isExam = true;
    } else if (cardId.includes("gmat")) {
      resolvedExamTab = "GMAT";
      isExam = true;
    } else if (cardId.includes("gre")) {
      resolvedExamTab = "GRE";
      isExam = true;
    } else if (cardId === "pets" || cardId === "pets-2023") {
      newTab = "pet";
    } else if (cardId === "pet-2024-exam") {
      resolvedExamTab = "PET";
      isExam = true;
    } else if (cardId.includes("ket")) {
      resolvedExamTab = "KET";
      isExam = true;
    } else if (cardId.includes("sat")) {
      resolvedExamTab = "SAT";
      isExam = true;
    } else if (cardId.includes("bec")) {
      resolvedExamTab = "BEC";
      isExam = true;
    } else if (cardId.includes("pte") || cardId.includes("wfd") || cardId.includes("listening-fib")) {
      resolvedExamTab = "PTE";
      isExam = true;
    } else if (cardId.includes("toeic")) {
      resolvedExamTab = "TOEIC";
      isExam = true;
    } else if (cardId.includes("cefr")) {
      resolvedExamTab = "CEFR";
      isExam = true;
    } else if (cardId.includes("oxford")) {
      resolvedExamTab = "牛津版";
      isExam = true;
    } else if (cardId.includes("voa")) {
      resolvedExamTab = "其他";
      isExam = true;
    } else if (cardId.includes("fce")) {
      resolvedExamTab = "FCE";
      isExam = true;
    } else if (cardId === "architecture" || cardId === "computer") {
      newTab = "professional";
    } else if (cardId.startsWith("self-")) {
      newTab = "self";
    } else {
      const isExamTabCard = examTabs.some((tab) =>
        examCardsData[tab.value]?.some((card) => card.id === cardId)
      );

      if (isExamTabCard) {
        for (const tab of examTabs) {
          if (examCardsData[tab.value]?.some((card) => card.id === cardId)) {
            resolvedExamTab = tab.value;
            isExam = true;
            break;
          }
        }
      } else {
        newTab = "other";
      }
    }

    setIsExamCategory(isExam);

    if (!isExam) {
      setSelectedTab((currentTab) => {
        if (currentTab === newTab) {
          return currentTab;
        }
        setTabChangedFromSettings(true);
        return newTab;
      });
    }

    if (resolvedExamTab) {
      setSelectedExamTab((currentExamTab) =>
        currentExamTab === resolvedExamTab ? currentExamTab : resolvedExamTab
      );
    }
  }, [selectedDictionary, settingsLoaded, reverseDictionaryMapping, examTabs, examCardsData]);

  // Fetch available dictionaries from API
  useEffect(() => {
    const fetchDictionaries = async () => {
      try {
        // If we have a cached value, reuse it and skip network
        if (DICT_CACHE) {
          setAvailableDictionaries(DICT_CACHE);
          setDictionariesLoading(false);
          return;
        }

        setDictionariesLoading(true);

        // If a request is already in-flight, await it
        if (DICT_IN_FLIGHT) {
          const dicts = await DICT_IN_FLIGHT;
          setAvailableDictionaries(dicts);
          return;
        }

        // Start new request and record in-flight promise
        DICT_IN_FLIGHT = (async () => {
          const response = await fetch("/api/user-settings/dictionaries", { cache: 'no-store' });
          if (!response.ok) {
            throw new Error(`Failed to fetch dictionaries: ${response.status}`);
          }
          const data = await response.json();
          const dicts: Dictionary[] = data.dictionaries || [];
          DICT_CACHE = dicts;
          return dicts;
        })();

        const dicts = await DICT_IN_FLIGHT;
        setAvailableDictionaries(dicts);
      } catch (error) {
        console.error("Error fetching dictionaries:", error);
        toast.error("Failed to load vocabulary options. Please refresh the page.");
      } finally {
        DICT_IN_FLIGHT = null;
        setDictionariesLoading(false);
      }
    };

    fetchDictionaries();
  }, []);

  // Fetch user's current dictionary setting
  useEffect(() => {
    const fetchUserSettings = async () => {
      if (!user?.id) {
        setSettingsLoaded(true); // 即使没有用户，也标记为已加载，确保显示默认tab
        return;
      }

      try {
        setIsLoading(true);
        setSettingsLoaded(false);

        const response = await fetch(`/api/user-settings?userId=${user.id}`);
        if (!response.ok) {
          throw new Error(`Failed to fetch user settings: ${response.status}`);
        }

        const settings = await response.json();
        console.log("Fetched user settings:", settings);
        setSelectedDictionary(settings.dictionarySetting || "");
        setSettingsLoaded(true);
      } catch (error) {
        console.error("Error fetching user settings:", error);
        toast.error("Failed to load your saved settings. Using default options.");
        setSettingsLoaded(true); // 即使出错，也标记为已加载，确保显示默认tab
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserSettings();
  }, [user]);

  // Clear messages function
  const clearMessages = useCallback(() => {
    setError(null);
    setSuccess(null);
  }, []);

  return {
    // State
    selectedTab,
    setSelectedTab,
    selectedExamTab,
    setSelectedExamTab,
    availableDictionaries,
    selectedDictionary,
    isLoading,
    dictionariesLoading,
    settingsLoaded,
    tabChangedFromSettings,
    setTabChangedFromSettings,
    isExamCategory,
    error,
    success,

    // Functions
    isCategorySelected,
    handleVocabularyCardClick,
    handleExamCardClick,
    updateDictionarySetting,
    handleRemoveSelection,
    clearMessages,

    // Constants
    examTabs,
    examCardsData,
    vocabularyCategories,
    kaoyanCategories,
    professionalCategories,
    petCategories,
    selfStudyCategories,
    otherCategories,
  };
};
